import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Load from '../../components/Load';
import AthletesTable from '../../components/admin/athletes/AthletesTable';
import {
  listarAtletas,
  deletarAtleta,
  Atleta,
} from '../../services/atletaService';
import { dispatchGlobalError } from '../../context/ApiErrorContext';
import AthletesTableToolbar from '../../components/admin/AthletesTableToolbar';
import type { AthletesFilter } from '../../types/atleta';
import { useQuery } from '@apollo/client';
import { LISTAR_CIDADES, Cidade } from '../../services/localizacaoService';

const AdminAthletesPage: React.FC = () => {
  const [athletes, setAthletes] = useState<Atleta[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [filters, setFilters] = useState<AthletesFilter>({});
  const navigate = useNavigate();

  const { data: cidadesData } = useQuery<{ cidades: Cidade[] }>(LISTAR_CIDADES);

  const normalizeFilters = useCallback((f: AthletesFilter) => {
    const onlyDigits = (v?: string | null) => (v ? v.replace(/\D+/g, '') : null);
    const toNull = (v?: string | null) => (v && v.trim() !== '' ? v : null);
    return {
      nomeUsuario: toNull(f.nomeUsuario || null),
      cpf: onlyDigits(f.cpf || null),
      cidade: toNull(f.cidade || null),
      status: f.status || null,
    } as AthletesFilter;
  }, []);

  const loadAthletes = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const normalized = normalizeFilters(filters);
      const data = await listarAtletas({
        page,
        size: rowsPerPage,
        nomeUsuario: normalized.nomeUsuario,
        cpf: normalized.cpf,
        cidadeId: normalized.cidade,
        ativo: normalized.status === 'ATIVO' ? true : normalized.status === 'INATIVO' ? false : null,
      });
      setAthletes(data.items);
      setTotal(data.totalItems);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters, normalizeFilters]);

  useEffect(() => {
    loadAthletes();
  }, [page, rowsPerPage, loadAthletes]);

  const handlePageChange = (p: number, size: number) => {
    setPage(p);
    setRowsPerPage(size);
  };

  const handleEdit = (id: string) => {
    navigate(`/admin/atletas/${id}`);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Deseja deletar este atleta?')) return;
    try {
      await deletarAtleta(id);
      alert('Atleta deletado com sucesso.');
      loadAthletes();
    } catch (e) {
      dispatchGlobalError('Erro ao deletar atleta.');
    }
  };

  let content: React.ReactNode;
  if (loading) {
    content = <Load />;
  } else if (error) {
    content = <div className="alert alert-danger">Erro ao carregar atletas.</div>;
  } else if (athletes.length === 0) {
    content = <div className="alert alert-info">Nenhum atleta encontrado.</div>;
  } else {
    content = (
      <AthletesTable
        athletes={athletes}
        page={page}
        rowsPerPage={rowsPerPage}
        total={total}
        onPageChange={handlePageChange}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    );
  }

  return (
    <Fragment>
      <div className="main-wrapper">
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="middle-wrap">
                <div className="card w-100 border-0 bg-white shadow-xs p-0 mb-4 rounded-3">
                  <div id='header-athletes' className="card-title d-flex flex-row pt-3">
                    <div className='col-1'>
                      <button
                        type="button"
                        className="btn  text-primary p-0 d-inline-block mt-2 me-2 ms-3"
                        onClick={() => window.history.back()}
                      >
                        <i className="ti-arrow-left font-sm"></i>
                      </button>
                    </div>
                    <div className='col-8 d-flex align-items-center'>
                      <h4 className="font-xs text-primary fw-600  mb-0 mt-2 flex-grow-1">
                        Atletas
                      </h4>
                    </div>
                    <div className='col-3 d-flex justify-content-end align-items-center me-3'>
                      <h4 className="font-xs text-primary fw-600 ms-2 mb-0 mt-2 flex-grow-1">
                        Total: {total}
                      </h4>
                    </div>

                  </div>
                  <div className="card-body p-4 w-100  border-0 d-flex flex-column  align-items-center">
                    <div className="row" id="toolbar-athletes">
                      <AthletesTableToolbar
                        availableFilters={['nomeUsuario','cpf','cidade','status']}
                        initialFilters={{}}
                        value={filters}
                        onChange={setFilters}
                        onApply={() => { setPage(0); loadAthletes(); }}
                        onReset={() => { setFilters({}); setPage(0); loadAthletes(); }}
                        fetchCidades={async (q: string) => {
                          const qn = (q || '').trim().toLowerCase();
                          const base = (cidadesData?.cidades || [])
                            .filter((c) => !qn || c.nome.toLowerCase().includes(qn) || c.uf.toLowerCase().includes(qn))
                            .map((c) => ({ label: `${c.nome} - ${c.uf}`, value: c.id }));
                          let list = base.slice(0, 50); // limit options for performance
                          // Garante que a cidade selecionada esteja presente nas opcoes
                          if (filters.cidade) {
                            const already = list.some(o => o.value === filters.cidade);
                            if (!already) {
                              const sel = (cidadesData?.cidades || []).find(c => c.id === filters.cidade);
                              if (sel) {
                                list = [{ label: `${sel.nome} - ${sel.uf}`, value: sel.id }, ...list].slice(0, 51);
                              }
                            }
                          }
                          return list;
                        }}
                        showRefreshButton
                        onRefresh={() => loadAthletes()}
                      />
                    </div>
                    {content}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default AdminAthletesPage;
